import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import countriesService from '../services/countriesService';
import useToast from '../hooks/useToast';
import Toast from './Toast';
import { getCountryFlag, getAllCountryNames } from '../data/countriesMapping';

const CountriesPage = () => {
  const [countries, setCountries] = useState([]);
  const [userSelections, setUserSelections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newCountryName, setNewCountryName] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedFlag, setSelectedFlag] = useState('🌍');
  const { toasts, hideToast, success, error: showError, info } = useToast();
  
  const allCountryNames = getAllCountryNames();

  useEffect(() => {
    const unsubscribeCountries = countriesService.subscribeToCountries((countriesList) => {
      // Автоматически определяем флаги для стран без флагов
      const countriesWithFlags = countriesList.map(country => {
        if (!country.flag && country.name) {
          const flag = getCountryFlag(country.name);
          // Обновляем флаг в базе данных, если он был определен
          if (flag && flag !== '🌍') {
            countriesService.updateCountry(country.id, { flag }).catch(err => {
              console.error('Ошибка обновления флага:', err);
            });
          }
          return { ...country, flag: flag || country.flag || '🌍' };
        }
        return country;
      });
      setCountries(countriesWithFlags);
      setLoading(false);
    });

    const unsubscribeSelections = countriesService.subscribeToUserSelections((selections) => {
      setUserSelections(selections);
    });

    countriesService.getAllCountries().then((countriesList) => {
      // Автоматически определяем флаги для стран без флагов
      const countriesWithFlags = countriesList.map(country => {
        if (!country.flag && country.name) {
          const flag = getCountryFlag(country.name);
          // Обновляем флаг в базе данных, если он был определен
          if (flag && flag !== '🌍') {
            countriesService.updateCountry(country.id, { flag }).catch(err => {
              console.error('Ошибка обновления флага:', err);
            });
          }
          return { ...country, flag: flag || country.flag || '🌍' };
        }
        return country;
      });
      setCountries(countriesWithFlags);
      setLoading(false);
    });

    countriesService.getUserSelectedCountries().then((selections) => {
      setUserSelections(selections);
    });

    return () => {
      unsubscribeCountries();
      unsubscribeSelections();
    };
  }, []);

  const handleCountryNameChange = (value) => {
    setNewCountryName(value);
    
    // Автоматически определяем флаг
    const flag = getCountryFlag(value);
    setSelectedFlag(flag);
    
    // Показываем подсказки при вводе
    if (value.trim().length > 0) {
      const filtered = allCountryNames.filter(name =>
        name.toLowerCase().includes(value.toLowerCase())
      ).slice(0, 5);
      setSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSelectSuggestion = (countryName) => {
    setNewCountryName(countryName);
    const flag = getCountryFlag(countryName);
    setSelectedFlag(flag);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const handleAddCountry = async (e) => {
    e.preventDefault();
    
    if (!newCountryName.trim()) {
      showError('Введите название страны');
      return;
    }

    const flag = getCountryFlag(newCountryName);
    const countryCode = ''; // Код больше не используется

    setLoading(true);
    const result = await countriesService.addCountry(
      newCountryName.trim(),
      countryCode,
      flag
    );

    if (result.success) {
      success(`Страна "${newCountryName}" добавлена!`);
      setNewCountryName('');
      setSelectedFlag('🌍');
      setShowSuggestions(false);
      setSuggestions([]);
      setShowAddForm(false);
    } else {
      showError(result.error || 'Ошибка при добавлении страны');
    }
    setLoading(false);
  };

  const handleDeleteCountry = async (countryId, countryName) => {
    const password = window.prompt('Введите пароль для удаления страны');
    if (password === null) {
      // Пользователь отменил ввод
      return;
    }

    if (password !== 'Qwerty$321') {
      showError('Неверный пароль. Удаление отменено.');
      return;
    }

    if (!window.confirm(`Удалить "${countryName}"?`)) {
      return;
    }

    setLoading(true);
    const result = await countriesService.deleteCountry(countryId);
    
    if (result.success) {
      success(`Страна "${countryName}" удалена`);
    } else {
      showError(result.error || 'Ошибка при удалении');
    }
    setLoading(false);
  };

  const handleToggleVote = async (country) => {
    const isSelected = userSelections.some(s => s.countryId === country.id);
    
    if (isSelected) {
      const result = await countriesService.unvoteForCountry(country.id);
      if (result.success) {
        info(`Убрано: ${country.name}`);
      }
    } else {
      const result = await countriesService.voteForCountry(country.id, country.name);
      if (result.success) {
        success(`Добавлено: ${country.name}`);
      }
    }
  };

  const isCountrySelected = (countryId) => {
    return userSelections.some(s => s.countryId === countryId);
  };

  const popularCountries = [...countries]
    .sort((a, b) => (b.votes || 0) - (a.votes || 0))
    .slice(0, 8);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 max-w-4xl">
        {/* Хлебные крошки */}
        <nav className="mb-4 sm:mb-6" aria-label="Breadcrumb">
          <ol className="flex items-center space-x-2 text-sm">
            <li>
              <Link
                to="/"
                className="text-gray-500 hover:text-primary-600 transition-colors flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                <span className="hidden sm:inline">Главная</span>
              </Link>
            </li>
            <li>
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </li>
            <li>
              <span className="text-gray-900 font-medium">Голосование</span>
            </li>
          </ol>
        </nav>

        {/* Компактный заголовок */}
        <div className="mb-4 sm:mb-6">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">
            🗳️ Голосуйте за VPN локации
          </h1>
          <p className="text-sm text-gray-600">
            Выбирайте страны для подписки
          </p>
        </div>

        {/* Компактная статистика */}
        <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-4 sm:mb-6">
          <div className="bg-white rounded-lg p-3 sm:p-4 border border-gray-200 text-center">
            <div className="text-lg sm:text-2xl font-bold text-blue-600">{countries.length}</div>
            <div className="text-xs sm:text-sm text-gray-600 mt-1">Стран</div>
          </div>
          <div className="bg-white rounded-lg p-3 sm:p-4 border border-gray-200 text-center">
            <div className="text-lg sm:text-2xl font-bold text-green-600">{userSelections.length}</div>
            <div className="text-xs sm:text-sm text-gray-600 mt-1">Ваш выбор</div>
          </div>
          <div className="bg-white rounded-lg p-3 sm:p-4 border border-gray-200 text-center">
            <div className="text-lg sm:text-2xl font-bold text-purple-600">
              {countries.reduce((sum, c) => sum + (c.votes || 0), 0)}
            </div>
            <div className="text-xs sm:text-sm text-gray-600 mt-1">Голосов</div>
          </div>
        </div>

        {/* Ваш выбор - компактный */}
        {userSelections.length > 0 && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
            <div className="flex items-center justify-between mb-2 sm:mb-3">
              <h2 className="text-sm sm:text-base font-semibold text-gray-900">
                Ваш выбор ({userSelections.length})
              </h2>
            </div>
            <div className="flex flex-wrap gap-2">
              {userSelections.map((selection) => {
                const country = countries.find(c => c.id === selection.countryId);
                if (!country) return null;
                
                return (
                  <button
                    key={selection.countryId}
                    onClick={() => handleToggleVote(country)}
                    className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 bg-green-500 text-white rounded-full text-xs sm:text-sm font-medium hover:bg-green-600 transition-colors"
                  >
                    {country.flag ? (
                      <span className="flag-emoji">{country.flag}</span>
                    ) : (
                      <span className="text-xs font-semibold text-gray-500">{country.code || '🌍'}</span>
                    )}
                    <span>{country.name}</span>
                    <span>×</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Компактная форма добавления */}
        <div className="bg-white rounded-lg border border-gray-200 p-3 sm:p-4 mb-4 sm:mb-6">
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="w-full flex items-center justify-between text-left"
          >
            <span className="text-sm sm:text-base font-medium text-gray-700">
              + Предложить страну
            </span>
            <svg className={`w-4 h-4 sm:w-5 sm:h-5 text-gray-400 transition-transform ${showAddForm ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {showAddForm && (
            <form onSubmit={handleAddCountry} className="mt-3 space-y-3">
              <div className="relative">
                <div className="flex items-center gap-2">
                  <div className="flex-shrink-0">
                    <span className="flag-emoji flag-emoji-large">{selectedFlag}</span>
                  </div>
                  <div className="flex-1 relative">
                    <input
                      type="text"
                      value={newCountryName}
                      onChange={(e) => handleCountryNameChange(e.target.value)}
                      onFocus={() => {
                        if (suggestions.length > 0) {
                          setShowSuggestions(true);
                        }
                      }}
                      onBlur={() => {
                        // Задержка для возможности клика по подсказке
                        setTimeout(() => setShowSuggestions(false), 200);
                      }}
                      placeholder="Введите название страны на русском *"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                      autoComplete="off"
                    />
                    {showSuggestions && suggestions.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {suggestions.map((suggestion, index) => (
                          <button
                            key={index}
                            type="button"
                            onClick={() => handleSelectSuggestion(suggestion)}
                            className="w-full px-3 py-2 text-left hover:bg-blue-50 flex items-center gap-2 text-sm"
                          >
                            <span className="flag-emoji">{getCountryFlag(suggestion)}</span>
                            <span>{suggestion}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                {newCountryName.trim() && (
                  <p className="text-xs text-gray-500 mt-1">
                    Флаг определится автоматически
                  </p>
                )}
              </div>
              <button
                type="submit"
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
                disabled={loading}
              >
                {loading ? 'Добавление...' : 'Добавить'}
              </button>
            </form>
          )}
        </div>

        {/* Компактный топ стран */}
        {popularCountries.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-3 sm:p-4 mb-4 sm:mb-6">
            <h2 className="text-sm sm:text-base font-semibold mb-3 flex items-center gap-2">
              <span>🔥</span> Топ локаций
            </h2>
            <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2 sm:gap-3">
              {popularCountries.map((country, index) => (
                <button
                  key={country.id}
                  onClick={() => handleToggleVote(country)}
                  className={`relative p-2 sm:p-3 rounded-lg border transition-all ${
                    isCountrySelected(country.id)
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 bg-gray-50 hover:border-blue-400 hover:bg-blue-50'
                  }`}
                >
                  {index < 3 && (
                    <div className="absolute -top-1 -right-1 w-4 h-4 sm:w-5 sm:h-5 bg-orange-500 text-white rounded-full flex items-center justify-center text-[10px] sm:text-xs font-bold">
                      {index + 1}
                    </div>
                  )}
                  <div className="mb-1 text-center">
                    {country.flag ? (
                      <span className="flag-emoji flag-emoji-large">{country.flag}</span>
                    ) : (
                      <span className="text-xs font-semibold text-gray-500">{country.code || '🌍'}</span>
                    )}
                  </div>
                  <div className="font-medium text-xs text-center mb-0.5 truncate">{country.name}</div>
                  <div className="text-center">
                    <span className={`text-[10px] sm:text-xs font-semibold ${isCountrySelected(country.id) ? 'text-green-700' : 'text-gray-600'}`}>
                      {country.votes || 0}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Компактный список стран */}
        <div className="bg-white rounded-lg border border-gray-200 p-3 sm:p-4">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <h2 className="text-sm sm:text-base font-semibold">Все страны ({countries.length})</h2>
          </div>

          {loading && countries.length === 0 ? (
            <div className="text-center py-6 sm:py-8">
              <div className="inline-block w-6 h-6 sm:w-8 sm:h-8 border-3 sm:border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
              <p className="mt-3 sm:mt-4 text-sm text-gray-600">Загрузка...</p>
            </div>
          ) : countries.length === 0 ? (
            <div className="text-center py-6 sm:py-8 text-gray-500">
              <p className="text-sm sm:text-base">Пока нет стран</p>
              <p className="text-xs sm:text-sm mt-1">Добавьте первую страну</p>
            </div>
          ) : (
            <div className="space-y-2">
              {countries.map((country) => (
                <div
                  key={country.id}
                  className={`p-3 sm:p-4 rounded-lg border transition-all ${
                    isCountrySelected(country.id)
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 bg-gray-50 hover:border-blue-400 hover:bg-blue-50'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2 sm:gap-3">
                    <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                      <div className="flex-shrink-0">
                        {country.flag ? (
                          <span className="flag-emoji flag-emoji-xl">{country.flag}</span>
                        ) : (
                          <span className="text-xs font-semibold text-gray-500 bg-gray-100 px-2 py-1 rounded">{country.code || '🌍'}</span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm sm:text-base text-gray-900 truncate">{country.name}</div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className={`text-xs font-medium ${isCountrySelected(country.id) ? 'text-green-700' : 'text-gray-600'}`}>
                            {country.votes || 0} голосов
                          </span>
                          {country.code && (
                            <span className="text-xs text-gray-400">• {country.code}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => handleToggleVote(country)}
                        className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                          isCountrySelected(country.id)
                            ? 'bg-green-500 text-white hover:bg-green-600'
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                      >
                        {isCountrySelected(country.id) ? '✓' : 'Выбрать'}
                      </button>
                      <button
                        onClick={() => handleDeleteCountry(country.id, country.name)}
                        className="p-1.5 sm:p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        title="Удалить"
                      >
                        <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Toast уведомления - мобильная оптимизация */}
      <div className="fixed top-2 right-2 sm:top-4 sm:right-4 z-50 space-y-2 max-w-[calc(100vw-1rem)]">
        {toasts.map(toast => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            duration={toast.duration}
            onClose={() => hideToast(toast.id)}
          />
        ))}
      </div>
    </div>
  );
};

export default CountriesPage;
